/**
 * The remastered sprites for one cave's colours, in a canvas, drawn through the same interface as
 * the C64 atlas. A sprite without a remastered version falls back to the C64 one.
 */

import { atlasFor } from '../atlas';
import { makeCanvas } from '../canvas';
import type { SpriteSheet } from '../sheet';
import type { SpriteName } from '../sprites';
import { rampSet } from './color';
import { N } from './px';
import { remasterFrames } from './sheet';

const PER_ROW = 16;

export class RemasterAtlas implements SpriteSheet {
  private readonly canvas: HTMLCanvasElement;
  private readonly index = new Map<SpriteName, { first: number; count: number }>();
  private readonly fallback: SpriteSheet;

  constructor(readonly colors: readonly [number, number, number]) {
    this.fallback = atlasFor(colors);
    const frames = remasterFrames(rampSet(colors));
    let total = 0;
    for (const [name, list] of Object.entries(frames)) {
      if (!list || list.length === 0) continue;
      this.index.set(name as SpriteName, { first: total, count: list.length });
      total += list.length;
    }
    this.canvas = makeCanvas(PER_ROW * N, Math.ceil(total / PER_ROW) * N);
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('no 2d context');
    for (const [name, list] of Object.entries(frames)) {
      const at = this.index.get(name as SpriteName);
      if (!at || !list) continue;
      list.forEach((px, i) => {
        const cell = at.first + i;
        ctx.putImageData(new ImageData(px.data, N, N), (cell % PER_ROW) * N, Math.floor(cell / PER_ROW) * N);
      });
    }
  }

  draw(ctx: CanvasRenderingContext2D, name: SpriteName, f: number, x: number, y: number): void {
    const at = this.index.get(name);
    if (!at) return this.fallback.draw(ctx, name, f, x, y);
    const cell = at.first + (((f % at.count) + at.count) % at.count);
    ctx.drawImage(this.canvas, (cell % PER_ROW) * N, Math.floor(cell / PER_ROW) * N, N, N, x, y, N, N);
  }
}

const cache = new Map<string, RemasterAtlas>();

export function remasterAtlasFor(colors: readonly [number, number, number]): RemasterAtlas {
  const key = colors.join(',');
  let atlas = cache.get(key);
  if (!atlas) {
    atlas = new RemasterAtlas(colors);
    cache.set(key, atlas);
  }
  return atlas;
}
